import { Link } from 'react-router-dom';
import { Users, Lock, ListOrdered, BarChart2, Settings, Play } from 'lucide-react';
import { AreaDocLayout, DocDivider, ReferenceTable } from '../components/AreaDocLayout';
import { DocSection } from '../components/DocSection';
import { ConceptCard } from '../components/ConceptCard';
import { StepGuide } from '../components/StepGuide';
import { SessionStateDiagram } from '../components/diagrams/SessionStateDiagram';

function DecisionClassCards() {
  const classes = [
    {
      name: 'selection',
      label: 'Selection',
      desc: 'Choose one or more items from a set. Produces a selected subset, not an ordered list.',
      color: 'bg-[var(--slate-dim)] border-[var(--slate)] text-[var(--text-sec)]',
      tag: 'Pick N',
    },
    {
      name: 'range_selection',
      label: 'Range Selection',
      desc: 'Select items within a defined range or band (e.g., top quartile, "must do this quarter").',
      color: 'bg-[var(--slate-dim)] border-[var(--slate)] text-[var(--text-sec)]',
      tag: 'Band',
    },
    {
      name: 'convergence',
      label: 'Convergence',
      desc: 'Align a group on a single value or score for an item. Used in values sessions to agree on what is true.',
      color: 'bg-[var(--slate-dim)] border-[var(--slate)] text-[var(--text-sec)]',
      tag: 'Align',
    },
    {
      name: 'force_ranking',
      label: 'Force Ranking',
      desc: 'Produce a strict comparative ordering of all items in the pool. This is where formal prioritization happens.',
      color: 'bg-[var(--green-dim)] border-[var(--green)] text-[var(--green)] dark:text-[var(--text-ter)]',
      tag: 'Strict order',
    },
  ];

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {classes.map((c) => (
        <div key={c.name} className={` border p-4 ${c.color}`}>
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm font-bold font-heading">{c.label}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-mono font-medium ${c.color}`}>{c.tag}</span>
          </div>
          <p className="text-xs text-[var(--text-sec)] leading-relaxed">{c.desc}</p>
          <p className="text-xs font-mono text-[var(--text-ter)] mt-1.5">{c.name}</p>
        </div>
      ))}
    </div>
  );
}

export default function SessionsDocs() {
  return (
    <AreaDocLayout
      icon={Users}
      iconBg="bg-[var(--slate-dim)] border-[var(--slate)]"
      iconColor="text-[var(--text-ter)]"
      title="Sessions"
      subtitle="The canonical unit of judgment work — structured execution that produces immutable, auditable results."
      tag="Work lane"
      tagColor="bg-[var(--slate-dim)] text-[var(--text-sec)]"
    >
      {/* Overview */}
      <DocSection
        title="Overview"
        description="Sessions are where the actual prioritization work happens. They are structured, not ad hoc."
        accent="blue"
      >
        <div className="bg-[var(--slate-dim)] border border-[var(--slate)] p-4 text-sm text-[var(--text-sec)] leading-relaxed">
          A <strong>Session</strong> is the structural container where prioritization work is performed. Sessions exist in the <strong>work lane</strong> — separate from the ball lane (<Link to="/prioritization/documentation/catchball" className="underline text-[var(--text-sec)]">Catchball</Link>).
          Each session declares an intent (its type), runs over a locked pool and criteria, and produces an <strong>immutable Session Result</strong>. Sessions belong to a <Link to="/prioritization/documentation/tracks" className="underline text-[var(--text-sec)]">Track</Link> when operating inside a cycle.
        </div>
      </DocSection>

      {/* State machine + types */}
      <DocSection
        title="Session States & Types"
        description="Sessions move through a defined state machine. The session type declares what is being evaluated."
        accent="blue"
      >
        <SessionStateDiagram />
      </DocSection>

      <DocDivider />

      {/* Decision classes */}
      <DocSection
        title="Decision Classes"
        description="Decision class defines the method of judgment inside a session — not what is being evaluated (that's the session type), but how the group makes its decision."
        accent="blue"
      >
        <DecisionClassCards />
        <div className="bg-[var(--mustard-dim)] border border-[var(--mustard)] p-3 mt-2">
          <p className="text-xs text-[var(--mustard)]">
            <strong>Important:</strong> Formal prioritization — the production of a strict comparative ordering — only occurs in sessions with <code className="font-mono bg-[var(--mustard-dim)] px-1 rounded">decision_class = force_ranking</code> and <code className="font-mono bg-[var(--mustard-dim)] px-1 rounded">session_type = items</code>. All other sessions enable prioritization but do not themselves produce a ranked list.
          </p>
        </div>
      </DocSection>

      <DocDivider />

      {/* Key concepts */}
      <DocSection title="Key Concepts" accent="blue">
        <div className="grid gap-3 md:grid-cols-2">
          <ConceptCard
            term="Session Result"
            definition="The immutable output of a completed session. Results are produced once and never mutated — they are the permanent record of what the group decided."
            icon={Lock}
            color="blue"
            tag="Immutable"
          />
          <ConceptCard
            term="Item Pool"
            definition="The locked set of items being evaluated in this session. The pool is confirmed during a pool configuration session before ranking begins."
            icon={ListOrdered}
            color="blue"
          />
          <ConceptCard
            term="Criteria"
            definition="The scoring dimensions used to evaluate items — e.g., Strategic Value, Confidence, Effort. Set in a criteria configuration session."
            icon={Settings}
            color="blue"
          />
          <ConceptCard
            term="Session Facilitator"
            definition="The person who configures and opens the session. Different from a participant who submits scores or rankings."
            icon={Users}
            color="blue"
          />
          <ConceptCard
            term="Run"
            definition="A single participant's execution within a session. When all runs are complete, the session moves to Run Complete state."
            icon={Play}
            color="blue"
          />
          <ConceptCard
            term="Proposal Eligibility"
            definition="A ranking session inside a cycle may be referenced by a proposal only if the session is fully completed and its result passes governance eligibility checks."
            icon={BarChart2}
            color="blue"
            tag="Cycle-linked"
          />
        </div>
      </DocSection>

      <DocDivider />

      {/* The two session categories explained visually */}
      <DocSection
        title="Configuration vs. Ranking"
        description="The two high-level session categories and when to use each."
        accent="blue"
      >
        <div className="grid gap-4 md:grid-cols-2">
          <div className="border border-[var(--slate)] bg-[var(--slate-dim)] p-4">
            <p className="text-xs font-bold text-[var(--text-sec)] uppercase tracking-wide mb-2">Configuration Sessions</p>
            <p className="text-sm text-[var(--text-sec)] mb-3">Set up the environment. Can run before or during a cycle. Produce configuration artifacts, not rankings.</p>
            <div className="space-y-1.5">
              {[
                { type: 'participants', desc: 'Who is in the session' },
                { type: 'item_pool', desc: 'What items are being evaluated' },
                { type: 'criteria', desc: 'What dimensions matter' },
                { type: 'scales', desc: 'Ranges and bands for each criterion' },
                { type: 'values', desc: 'Converge what is true about items' },
              ].map((t) => (
                <div key={t.type} className="flex items-center gap-2">
                  <code className="text-xs font-mono text-[var(--text-sec)] bg-[var(--slate-dim)] px-1.5 py-0.5 rounded">{t.type}</code>
                  <span className="text-xs text-[var(--text-sec)]">{t.desc}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="border border-[var(--green)] bg-[var(--green-dim)] p-4">
            <p className="text-xs font-bold text-[var(--green)] dark:text-[var(--text-ter)] uppercase tracking-wide mb-2">Ranking Sessions</p>
            <p className="text-sm text-[var(--text-sec)] mb-3">Produce a strict comparative ordering. Require all configuration to be complete first. Trigger governance eligibility.</p>
            <div className="space-y-1.5">
              {[
                { type: 'items', desc: 'Force-rank items against each other' },
              ].map((t) => (
                <div key={t.type} className="flex items-center gap-2">
                  <code className="text-xs font-mono text-[var(--green)] dark:text-[var(--text-ter)] bg-[var(--green-dim)] px-1.5 py-0.5 rounded">{t.type}</code>
                  <span className="text-xs text-[var(--text-sec)]">{t.desc}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 bg-[var(--green-dim)] border border-[var(--green)] p-3">
              <p className="text-xs text-[var(--green)] dark:text-[var(--text-ter)]">
                <strong>Required before force ranking:</strong> item pool locked, criteria selected, scales confirmed, values complete for all items in pool.
              </p>
            </div>
          </div>
        </div>
      </DocSection>

      <DocDivider />

      {/* Common tasks */}
      <DocSection title="Common Tasks" accent="blue">
        <div className="grid gap-6 md:grid-cols-2">
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
            <p className="text-sm font-semibold text-[var(--text-pri)] font-heading mb-3">Configure a Session</p>
            <StepGuide color="blue" steps={[
              { title: 'Open the Track', description: 'Navigate to the cycle, open the relevant Track.' },
              { title: 'Create a configuration session', description: 'Add sessions for participants, item pool, criteria, scales, and values in order.' },
              { title: 'Complete each session', description: 'Each type must reach Published state before ranking can begin.' },
              { title: 'Verify pool is locked', description: 'The item pool must be confirmed — items added after locking are not eligible.', note: 'Pool lock is explicit, not automatic.' },
            ]} />
          </div>

          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
            <p className="text-sm font-semibold text-[var(--text-pri)] font-heading mb-3">Run a Ranking Session</p>
            <StepGuide color="blue" steps={[
              { title: 'Confirm readiness', description: 'All configuration sessions must be complete. Check the Track readiness indicator.' },
              { title: 'Create a ranking session', description: 'Session type: items, decision class: force_ranking.' },
              { title: 'Open the session', description: 'Move the session to OPEN. Participants are notified and can begin submitting their rankings.' },
              { title: 'Close when complete', description: 'Once all runs are submitted, close the session. The system produces an immutable Session Result.' },
              { title: 'Review results', description: 'The ranked output is visible in the Session Results tab and is now available for proposal reference.' },
            ]} />
          </div>
        </div>
      </DocSection>

      <DocDivider />

      {/* Reference */}
      <DocSection title="Session Field Reference" accent="blue">
        <ReferenceTable rows={[
          { field: 'id', type: 'uuid', description: 'Unique session identifier.' },
          { field: 'session_type', type: 'enum', description: 'participants | item_pool | criteria | scales | values | items' },
          { field: 'decision_class', type: 'enum', description: 'selection | range_selection | convergence | force_ranking' },
          { field: 'state', type: 'enum', description: 'DRAFT | OPEN | RUN_COMPLETE | RESULTS_READY | PUBLISHED' },
          { field: 'track_instance_id', type: 'uuid?', description: 'Track instance this session belongs to (null for standalone sessions).' },
          { field: 'cycle_id', type: 'uuid?', description: 'Cycle this session is part of.' },
          { field: 'workspace_id', type: 'uuid', description: 'Workspace this session belongs to.' },
          { field: 'created_at', type: 'timestamp', description: 'When the session was created.' },
        ]} />
      </DocSection>
    </AreaDocLayout>
  );
}
